import { getContext } from '@fiverr-private/fiverr_context';
import { uid } from '@fiverr-private/futile';
import { count } from '../utils';
import { FORETHOUGHT } from '../constants';
import { forethoughtChatTriggeredEvent } from '../bi';
import { logger } from '../logger';
import { DEFAULT_LOCALE, VARIABLES_MAPPING } from './constants';

export const TOKEN_TIMEOUT = 1000 * 60 * 9;

class Forethought {
    constructor(config) {
        this.scriptAttributes = {
            id: 'forethought-widget-embed-script',
            src: 'https://solve-widget.forethought.ai/embed.js',
            type: 'application/javascript',
            'initial-intent-id': 'fa73df7a-71e0-4f49-8506-92816dcf460d',
        };
        this.__initialized = false;
        this.config = config;
        this.refetchInterval = null;

        this.initChat(config);
    }

    initChat = async (config) => {
        logger.debug('[Forethought] initChat called with config:', config);
        count(FORETHOUGHT.CHAT_SCRIPT_INITIATE);

        if (!config) {
            logger.debug('[Forethought] Invalid config, returning');
            return;
        }

        if (config && config.state === 'HIDDEN') {
            this.closeChatIfOpen(config);
            return;
        }

        if (this.__initialized) {
            this.toggleChat(config);
        } else {
            const { apiKey, token } = config.forethoughtConfig || {};
            logger.debug('[Forethought] Initializing script with API key:', apiKey);
            this.initScript(config, apiKey, token);
        }

        window.toggleChat = this.toggleChat;

        // Initialize global Chat object if widget is already loaded
        if (this.__initialized && window.Forethought && typeof window.Forethought === 'function') {
            this.initGlobalChatObject();
        }
    };

    initScript = (config, apiKey, token) => {
        const existingForethoughtScript = document.getElementById(this.scriptAttributes.id);
        const username = window.dataLayer && window.dataLayer[0].user_name;
        const userId = getContext().userId;
        const rawLocale = getContext().computedLocale;
        const locale = rawLocale?.toLowerCase()?.split('-')?.at(0) || DEFAULT_LOCALE;

        if (existingForethoughtScript) {
            return;
        }

        try {
            const $forethoughtScript = document.createElement('script');
            Object.entries(this.scriptAttributes).forEach(([k, v]) => {
                $forethoughtScript.setAttribute(k, v);
            });

            $forethoughtScript.setAttribute('data-ft-user-name', username || '');
            $forethoughtScript.setAttribute('data-ft-user-id', userId || '');

            if (userId) {
                $forethoughtScript.setAttribute('config-ft-persistence-id', String(userId));
            }

            // Add data-api-key as required by official docs
            if (apiKey) {
                $forethoughtScript.setAttribute('data-api-key', apiKey);
            }

            // Add token if provided
            if (token) {
                $forethoughtScript.setAttribute('data-ft-JWToken', token);
            }

            $forethoughtScript.setAttribute(
                'config-ft-ignore-and-update-persistence-parameters',
                'data-ft-JWToken|data-ft-jwtoken|full-screen|hidden|data-ft-user-name|data-ft-initialUrl|data-ft-user-id'
            );
            $forethoughtScript.setAttribute('data-ft-Embed-Script-Language', locale);
            $forethoughtScript.setAttribute('data-ft-initialUrl', window.location.href);

            // Add display attributes
            if (config.state === 'OPEN') {
                $forethoughtScript.setAttribute('full-screen', 'true');
            }
            if (config.state === 'HIDDEN') {
                $forethoughtScript.setAttribute('hidden', 'true');
            }

            // Listen for widget loaded event
            this.initWidgetEventListeners(config);

            document.head.appendChild($forethoughtScript);

            count(FORETHOUGHT.CHAT_SCRIPT_INITIATED);

            this.initTokenRefetch();
        } catch (e) {
            count(FORETHOUGHT.CHAT_SCRIPT_FAILED);
            logger.error('Failed to initialize Forethought script', e);
            throw e;
        }
    };

    // eslint-disable-next-line no-unused-vars
    initWidgetEventListeners(_config) {
        const context = getContext();
        const localUid = uid();

        window.addEventListener('message', (event) => {
            if (!event.data || !event.data.event) {
                return;
            }

            switch (event.data.event) {
                case 'forethoughtWidgetLoaded':
                    logger.debug('[Forethought] Widget loaded event received');
                    count(FORETHOUGHT.CHAT_WIDGET_INITIATED);
                    this.__initialized = true;
                    this.initGlobalChatObject();
                    break;

                case 'forethoughtWidgetOpened':
                    this.setStylesForOpenedChat();
                    forethoughtChatTriggeredEvent({ userId: context.userId, uid: localUid });
                    this.initGlobalChatObject();
                    count(FORETHOUGHT.CHAT_WIDGET_OPENED);
                    break;

                case 'forethoughtWidgetClosed':
                    count(FORETHOUGHT.CHAT_WIDGET_CLOSED);
                    this.setStylesForClosedChat();
                    break;

                case 'forethoughtWidgetError':
                    count(FORETHOUGHT.CHAT_WIDGET_FAILED);
                    logger.error(`Forethought widget error ${event.data.error}`);
                    break;
                default:
                    // Ignore other events
                    break;
            }
        });
    }

    updateConversationContext(contextData) {
        if (!window.Forethought || typeof window.Forethought !== 'function') {
            logger.warn('Forethought not initialized, cannot update context');
            return;
        }

        const filteredConfig = Object.entries(contextData).reduce((acc, [key, value]) => {
            if (VARIABLES_MAPPING[key] !== undefined) {
                acc[VARIABLES_MAPPING[key]] = value;
            }
            return acc;
        }, {});

        try {
            window.Forethought('widget', 'updateConversationContext', filteredConfig);
        } catch (e) {
            logger.error('Failed to update conversation context', e);
        }
    }

    setStylesForChatBot(state) {
        const $chatFrame = document.getElementById('forethought-chat');

        if (!$chatFrame) {
            return;
        }

        if (state === 'OPENED') {
            $chatFrame.classList.remove('forethought-chat-closed');
            $chatFrame.classList.add('forethought-chat-opened');
        } else {
            $chatFrame.classList.remove('forethought-chat-opened');
            $chatFrame.classList.add('forethought-chat-closed');
        }
    }

    setStylesForClosedChat() {
        this.setStylesForChatBot('CLOSED');
    }

    setStylesForOpenedChat() {
        this.setStylesForChatBot('OPENED');
    }

    toggleChat = (config = {}) => {
        if (!window.Forethought || typeof window.Forethought !== 'function') {
            this.initChat(config);
            return;
        }

        window.Forethought('widget', 'open');

        this.updateConversationContext(config);
    };

    closeChatIfOpen() {
        if (window.Forethought && typeof window.Forethought === 'function') {
            window.Forethought('widget', 'close');
            window.Forethought('widget', 'hide');
        }
    }

    triggerCampaign = async (campaignName, props = {}) => {
        try {
            if (!window.Forethought || typeof window.Forethought !== 'function') {
                logger.warn('[Forethought] Widget not initialized, cannot trigger campaign');
                return;
            }

            // Update conversation context with props
            if (props && Object.keys(props).length > 0) {
                this.updateConversationContext(props);
            }

            // Forethought uses triggerEventComplete instead of triggerCampaign
            // The identifier should match the event name configured in Forethought
            window.Forethought('widget', 'triggerEventComplete', {
                identifier: campaignName,
                payload: props,
            });

            count(`${FORETHOUGHT.CHAT_WIDGET_CAMPAIGN_TRIGGERED}.${campaignName}`);
        } catch (err) {
            logger.warn(err, {
                campaignName,
                source: 'Chat-triggerCampaign',
            });

            count(`${FORETHOUGHT.CHAT_WIDGET_CAMPAIGN_FAILED}.${campaignName}`);
        }
    };

    initTokenRefetch() {
        this.refetchInterval = setInterval(() => this.applyToken(), TOKEN_TIMEOUT);
    }

    async applyToken() {
        if (!window.Forethought || !this.__initialized) {
            return;
        }

        const token = await this.getToken();

        window.Forethought('widget', 'updateConversationContext', { [VARIABLES_MAPPING.JWToken]: token });
    }

    async getToken() {
        try {
            const response = await window.axios.get('/chat/support/config');

            if (!response.data?.token?.length || response.data.token === 'EMPTY') {
                throw new Error('Forethought token is empty');
            }

            return response.data.token;
        } catch (e) {
            if (this.refetchInterval) {
                clearInterval(this.refetchInterval);
            }
            logger.warn(e);
        }
    }

    initGlobalChatObject() {
        window.Chat = {
            toggle: this.toggleChat,
            triggerCampaign: this.triggerCampaign,
        };
    }
}

export default Forethought;
